import {
    useCallback,
    useEffect,
    useRef,
    useState,
    useId,
    type ChangeEvent,
    type FC,
    type FocusEvent,
    type HTMLAttributes,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import classnames from 'classnames';
import ErrorIcon from 'dibs-icons/exports/legacy/Exclamation.ts';
import ErrorIconCircle from 'dibs-icons/exports/legacy/ExclamationCircle.ts';
import ValidatedIcon from 'dibs-icons/exports/legacy/Checkmark.ts';
import { ErrorMessage } from '../ErrorMessage/index.tsx';
import { Field } from '../Common/Field/Field.tsx';
import { FormLabel } from '../Common/FormLabel/FormLabel.tsx';
import { InputBorderContainer } from '../Common/InputBorderContainer/InputBorderContainer.tsx';
import { AnimatedPlaceholderContainer } from '../Common/AnimatedPlaceholderContainer/AnimatedPlaceholderContainer.tsx';
import { TextInputContainer } from '../Common/TextInputContainer/TextInputContainer.tsx';
import { SIZES, DIRECTIONS } from '../Common/constants.ts';

import styles from './main.scss';
import dibsCss from 'dibs-css';

export type InputProps = {
    autoCapitalize?: boolean;
    autoCorrect?: boolean;
    autoComplete?: boolean | string;
    autoFocus?: boolean;
    className?: string;
    dataTn: string;
    disabled?: boolean;
    errorDataTn?: string;
    useCircularErrorIcon?: boolean;
    errorMessage?: ReactNode | { __html: string };
    errorMessageAlignment?: typeof DIRECTIONS.right | typeof DIRECTIONS.left;
    hasAnimatedPlaceholder?: boolean;
    hasError?: boolean;
    hasValidText?: boolean;
    horizontalSpacing?: typeof SIZES.def | typeof SIZES.small | typeof SIZES.large;
    inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
    highlightColor?: string;
    label?: ReactNode;
    leftMargin?: typeof SIZES.small;
    leftDecorator?: ReactNode;
    leftDecoratorClass?: string;
    maskForPrivacy?: boolean;
    max?: number | string;
    maxLength?: number;
    min?: number | string;
    name?: string;
    noBorder?: boolean;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    pattern?: string;
    placeholder?: string;
    readOnly?: boolean;
    rightDecorator?: ReactNode;
    rightDecoratorClass?: string;
    showIcons?: boolean;
    size?: typeof SIZES.large | typeof SIZES.medium | typeof SIZES.small;
    spellCheck?: boolean;
    step?: string;
    type?: string;
    defaultValue?: string;
    value?: string;
    title?: string;
    ariaControls?: string;
    ariaDescribedBy?: string;
    ariaLabel?: string | null;
    multiple?: boolean;
    accept?: string;
    /** @deprecated use inputRefCallback instead */
    inputRef?: (input: HTMLInputElement) => void;
    inputRefCallback?: (input: HTMLInputElement) => void;
    isHeaderInput?: boolean;
    /** must be universally unique among HTML element ids */
    id?: string;
};

export const Input: FC<InputProps> = ({
    accept,
    autoCapitalize = true,
    autoComplete = '',
    autoCorrect = true,
    autoFocus = false,
    className,
    dataTn,
    disabled = false,
    errorDataTn,
    errorMessage,
    errorMessageAlignment = DIRECTIONS.left,
    useCircularErrorIcon,
    hasAnimatedPlaceholder = false,
    hasError = false,
    hasValidText,
    horizontalSpacing = SIZES.def,
    inputMode,
    highlightColor,
    label,
    leftDecorator,
    leftDecoratorClass,
    leftMargin,
    maskForPrivacy = false,
    max,
    maxLength,
    min,
    multiple,
    name,
    onBlur,
    onChange,
    onFocus,
    noBorder = false,
    isHeaderInput = false,
    onKeyDown,
    pattern,
    placeholder,
    readOnly = false,
    rightDecorator,
    rightDecoratorClass,
    size,
    showIcons = true,
    spellCheck = true,
    step,
    type = 'text',
    title,
    defaultValue,
    value,
    ariaControls,
    ariaDescribedBy,
    ariaLabel,
    inputRef,
    inputRefCallback,
    id: propId,
}) => {
    const [isFocused, setIsFocused] = useState(!!autoFocus);
    const inputNodeRef = useRef<HTMLInputElement | null>(null);
    const uid = useId();

    // initial call to focus element if autoFocus is passed in as true
    useEffect(() => {
        if (autoFocus && inputNodeRef.current?.focus) {
            inputNodeRef.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>): void => {
            setIsFocused(false);
            onBlur?.(event);
        },
        [onBlur]
    );

    const handleFocus = useCallback(
        (event: FocusEvent<HTMLInputElement>): void => {
            setIsFocused(true);
            onFocus?.(event);
        },
        [onFocus]
    );

    const shouldShowAnimatedPlaceholder = (): boolean => {
        if (hasAnimatedPlaceholder && !!placeholder) {
            if (!value) {
                return isFocused && !readOnly;
            }
            return true;
        }
        return false;
    };

    const showError = hasError || !!errorMessage;
    const showErrorIcon = showError;
    const showSuccessIcon = !showErrorIcon && hasValidText;
    const shouldShowIcon = showIcons && (showErrorIcon || showSuccessIcon);
    const errorDataTnString = errorDataTn ? errorDataTn : `${dataTn}-error`;

    const leftDecoratorClasses = classnames(styles.decorator, leftDecoratorClass);
    const rightDecoratorClasses = classnames(styles.decorator, rightDecoratorClass);

    let autoCompleteString = '';
    if (typeof autoComplete === 'boolean') {
        autoCompleteString = autoComplete ? 'on' : 'off';
    } else if (typeof autoComplete === 'string') {
        autoCompleteString = autoComplete;
    }

    const autoCorrectString: string = autoCorrect ? 'on' : 'off';
    const autoCapitalizeString: string = autoCapitalize ? 'on' : 'off';
    const spacing = horizontalSpacing === SIZES.small ? SIZES.small : SIZES.medium;
    /* NOTE ON PLACEHOLDERS
        input element placeholder attributes are not accessible. render the
        placeholder text in a hidden span and and associate it to the input
        with aria-describedby on the input. display: none is invisible to
        the accessibility tree except when used with attributes like
        aria-describedby
    */

    // allow null ariaLabel
    const derivedAriaLabel = ariaLabel !== null && !label ? ariaLabel || placeholder : undefined;

    const internalUid = uid ? `input-${uid}` : undefined;
    const inputId = propId || internalUid;
    // only render hidden placeholder if it can be related via
    // id, has truthy value, will not be used in place of label
    // or ariaLabel, and is not redundant with label or
    // ariaLabel
    const renderHiddenPlaceholder =
        !!(uid && placeholder) &&
        (label || ariaLabel) &&
        placeholder !== label &&
        placeholder !== ariaLabel;
    const placeholderId = renderHiddenPlaceholder ? `input-placeholder-${uid}` : undefined;

    const describedBy = [ariaDescribedBy, placeholderId].filter(Boolean).join(' ') || undefined;
    let marginLeft = leftDecorator ? SIZES.none : spacing;
    if (leftMargin) {
        marginLeft = leftMargin;
    }
    return (
        <Field>
            <FormLabel htmlFor={inputId}>{label}</FormLabel>
            {renderHiddenPlaceholder && (
                <span id={placeholderId} className={styles.hiddenPlaceholder}>
                    {placeholder}
                </span>
            )}
            <InputBorderContainer
                disabled={disabled}
                hasError={showError}
                isFocused={isFocused}
                noBorder={noBorder}
                borderRounded={isHeaderInput}
                size={size}
                maskForPrivacy={maskForPrivacy}
                onClick={() => {
                    inputNodeRef.current?.focus();
                }}
            >
                {leftDecorator && <div className={leftDecoratorClasses}>{leftDecorator}</div>}
                <AnimatedPlaceholderContainer
                    marginLeft={marginLeft}
                    marginRight={rightDecorator ? SIZES.none : spacing}
                    placeholder={placeholder}
                    showAnimatedPlaceholder={shouldShowAnimatedPlaceholder()}
                >
                    {({ placeholderDidRender }) => (
                        <TextInputContainer
                            paddingTop={
                                placeholderDidRender ? SIZES.animatedPlaceholder : SIZES.none
                            }
                            isHeaderInput={isHeaderInput}
                            isHighlighted={!!highlightColor}
                        >
                            {/* There's a bug on FF and Safari where the `first-line` pseudo selector
                                            doesn't actually select input content: https://stackoverflow.com/a/22131244
                                            Best bet is to have a hidden container with the same value and a bg color */}
                            {highlightColor && (
                                <div
                                    className={classnames(
                                        highlightColor,
                                        dibsCss.absolute,
                                        dibsCss.selfCenter
                                    )}
                                >
                                    <span className={dibsCss.invisible}>{value}</span>
                                </div>
                            )}
                            <input
                                autoCapitalize={autoCapitalizeString}
                                autoComplete={autoCompleteString}
                                autoCorrect={autoCorrectString}
                                autoFocus={autoFocus}
                                className={className}
                                data-tn={dataTn}
                                disabled={disabled}
                                id={inputId}
                                inputMode={inputMode}
                                max={max || undefined}
                                maxLength={maxLength || undefined}
                                min={min || undefined}
                                name={name}
                                onBlur={handleBlur}
                                onChange={onChange}
                                onFocus={handleFocus}
                                onKeyDown={onKeyDown}
                                pattern={pattern}
                                placeholder={placeholderDidRender ? '' : placeholder}
                                readOnly={readOnly}
                                ref={input => {
                                    inputNodeRef.current = input;
                                    if (input && inputRef) {
                                        inputRef(input);
                                    }
                                    if (input && inputRefCallback) {
                                        inputRefCallback(input);
                                    }
                                }}
                                spellCheck={spellCheck}
                                step={step}
                                type={type}
                                defaultValue={defaultValue}
                                value={value}
                                aria-controls={ariaControls}
                                aria-describedby={describedBy}
                                aria-label={derivedAriaLabel}
                                title={title}
                                multiple={multiple}
                                accept={accept}
                            />
                        </TextInputContainer>
                    )}
                </AnimatedPlaceholderContainer>

                {shouldShowIcon && (
                    <div
                        className={classnames(styles.iconContainer, {
                            [styles.iconContainerSmall]: horizontalSpacing === SIZES.small,
                            [styles.iconContainerLarge]: horizontalSpacing === SIZES.large,
                        })}
                    >
                        {showErrorIcon && (
                            <>
                                {useCircularErrorIcon ? (
                                    <ErrorIconCircle className={styles.errorIcon} />
                                ) : (
                                    <ErrorIcon className={styles.errorIcon} />
                                )}
                            </>
                        )}
                        {showSuccessIcon && <ValidatedIcon className={styles.validatedIcon} />}
                    </div>
                )}
                {rightDecorator && <div className={rightDecoratorClasses}>{rightDecorator}</div>}
            </InputBorderContainer>
            <ErrorMessage
                alignRight={errorMessageAlignment === DIRECTIONS.right}
                message={errorMessage}
                dataTn={errorDataTnString}
            />
        </Field>
    );
};
